import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getConversations, getMessages } from "../apis/chat";
import { initSocket, getSocket, disconnectSocket } from "../utils/socket";
import moment from "moment-timezone";

const LiveChatContext = createContext(null);

/**
 * Format timestamp helper
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const now = moment().tz("Asia/Kolkata");
  const msgTime = moment(timestamp).tz("Asia/Kolkata");
  const diffMinutes = now.diff(msgTime, "minutes");
  const diffHours = now.diff(msgTime, "hours");
  const diffDays = now.diff(msgTime, "days");

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return msgTime.format("MMM DD, YYYY");
};

/**
 * Format last message text for display
 */
const formatLastMessageText = (lastMessage) => {
  if (!lastMessage) return "No messages";
  
  if (lastMessage.media_url) {
    const mediaType = lastMessage.message_type || "media";
    const mediaTypeLabels = {
      image: "📷 Image",
      video: "🎥 Video",
      audio: "🎵 Audio",
      document: "📄 Document",
      sticker: "🎨 Sticker",
    };
    let text = mediaTypeLabels[mediaType] || "📎 Media";
    if (lastMessage.message_text) {
      text += `: ${lastMessage.message_text}`;
    }
    return text;
  }
  
  if (lastMessage.message_type === "button") {
    if (lastMessage.button?.text) {
      return `🔘 ${lastMessage.button.text}`;
    }
    if (lastMessage.message_text) {
      return `🔘 ${lastMessage.message_text}`;
    }
    return "🔘 Button Interaction";
  }
  
  return lastMessage.message_text || "No messages";
};

/**
 * LiveChat Provider Component
 */
export const LiveChatProvider = ({ children }) => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedPhoneId, setSelectedPhoneId] = useState(null);
  
  // Store real-time messages (received via socket but not yet in database)
  // Key: `${customerPhone}_${phoneNumberId}`, Value: array of messages
  const realtimeMessagesRef = useRef(new Map());
  
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  /**
   * Fetch conversations from API and join all chat rooms
   */
  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getConversations(selectedPhoneId);
      if (response.data.success) {
        const formattedChats = response.data.data.map((chat) => {
          const lastMessageAt = chat.last_message?.sent_at || chat.last_message_at;
          return {
            id: `${chat.customer_phone}_${chat.phone_number_id}`,
            customer_phone: chat.customer_phone,
            phone_number_id: chat.phone_number_id,
            name: chat.customer_name || chat.customer_phone,
            phone: chat.customer_phone,
            lastMessage: formatLastMessageText(chat.last_message),
            timestamp: formatTimestamp(lastMessageAt),
            unread: chat.unread_count || 0,
            is_window_open: chat.is_window_open !== undefined ? chat.is_window_open : true,
            last_message: chat.last_message || null,
            last_message_at: lastMessageAt,
          };
        });
        
        // Sort by last_message_at descending (newest first)
        formattedChats.sort((a, b) => {
          const timeA = new Date(a.last_message_at || 0).getTime();
          const timeB = new Date(b.last_message_at || 0).getTime();
          return timeB - timeA;
        });
        
        setChats(formattedChats);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedPhoneId, user.id]);

  /**
   * Fetch messages for a conversation
   * Merges database messages with real-time messages (received via socket but not yet in DB)
   */
  const fetchMessages = useCallback(async (customerPhone, phoneNumberId) => {
    try {
      if (!customerPhone || !phoneNumberId) {
        console.error("Missing parameters for fetchMessages:", { customerPhone, phoneNumberId });
        return;
      }
      setMessagesLoading(true);
      
      // Fetch messages from database
      const response = await getMessages(customerPhone, phoneNumberId, 50, 0);
      if (response.data.success) {
        const dbMessages = response.data.data.messages || [];
        
        // Normalize sent_at for database messages (MySQL returns Date objects or strings)
        const normalizedDbMessages = dbMessages.map(msg => {
          let normalizedSentAt = msg.sent_at;
          if (normalizedSentAt) {
            if (normalizedSentAt instanceof Date) {
              normalizedSentAt = normalizedSentAt.toISOString();
            } else if (typeof normalizedSentAt === 'string') {
              const parsed = new Date(normalizedSentAt);
              if (!isNaN(parsed.getTime())) {
                normalizedSentAt = parsed.toISOString();
              }
            }
          }
          return {
            ...msg,
            sent_at: normalizedSentAt || msg.sent_at,
          };
        });
        
        // Get real-time messages for this chat (messages received via socket but not yet in DB)
        const chatKey = `${customerPhone}_${phoneNumberId}`;
        const realtimeMessages = realtimeMessagesRef.current.get(chatKey) || [];
        
        // Normalize sent_at for real-time messages
        const normalizedRealtimeMessages = realtimeMessages.map(msg => {
          let normalizedSentAt = msg.sent_at;
          if (normalizedSentAt) {
            if (normalizedSentAt instanceof Date) {
              normalizedSentAt = normalizedSentAt.toISOString();
            } else if (typeof normalizedSentAt === 'string') {
              const parsed = new Date(normalizedSentAt);
              if (!isNaN(parsed.getTime())) {
                normalizedSentAt = parsed.toISOString();
              }
            }
          }
          return {
            ...msg,
            sent_at: normalizedSentAt || msg.sent_at,
          };
        });
        
        // Merge database messages with real-time messages
        // Create a map to track messages by message_id to avoid duplicates
        const messagesMap = new Map();
        
        // Add database messages first
        normalizedDbMessages.forEach(msg => {
          if (msg.message_id) {
            messagesMap.set(msg.message_id, msg);
          }
        });
        
        // Add real-time messages (they will overwrite DB messages if duplicate, which is fine)
        normalizedRealtimeMessages.forEach(msg => {
          if (msg.message_id) {
            messagesMap.set(msg.message_id, msg);
          }
        });
        
        // Convert map to array and normalize sent_at to ensure consistent sorting
        const mergedMessages = Array.from(messagesMap.values()).map(msg => {
          // Normalize sent_at to ISO string for consistent sorting
          let normalizedSentAt = msg.sent_at;
          if (normalizedSentAt) {
            // If it's a Date object, convert to ISO string
            if (normalizedSentAt instanceof Date) {
              normalizedSentAt = normalizedSentAt.toISOString();
            }
            // If it's a timestamp (number), convert to ISO string
            else if (typeof normalizedSentAt === 'number') {
              normalizedSentAt = new Date(normalizedSentAt * 1000).toISOString();
            }
            // If it's already a string, ensure it's valid
            else if (typeof normalizedSentAt === 'string') {
              // Try to parse and convert to ISO if needed
              const parsed = new Date(normalizedSentAt);
              if (!isNaN(parsed.getTime())) {
                normalizedSentAt = parsed.toISOString();
              }
            }
          }
          return {
            ...msg,
            sent_at: normalizedSentAt || msg.sent_at,
          };
        });
        
        // Sort by sent_at ascending (oldest first, newest at bottom)
        mergedMessages.sort((a, b) => {
          const timeA = new Date(a.sent_at || a.timestamp || 0).getTime();
          const timeB = new Date(b.sent_at || b.timestamp || 0).getTime();
          
          // Debug log for timing issues
          if (isNaN(timeA) || isNaN(timeB)) {
            console.warn("Invalid timestamp detected:", {
              msgA: { message_id: a.message_id, sent_at: a.sent_at, timestamp: a.timestamp },
              msgB: { message_id: b.message_id, sent_at: b.sent_at, timestamp: b.timestamp },
            });
          }
          
          return timeA - timeB; // Ascending: oldest first, newest at bottom
        });
        
        console.log(`📋 Merged ${mergedMessages.length} messages (${normalizedDbMessages.length} from DB, ${normalizedRealtimeMessages.length} real-time)`);
        setMessages(mergedMessages);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  /**
   * Update conversation in chats list
   */
  const updateConversation = useCallback((messageData) => {
    const customerPhone = messageData.direction === "inbound" 
      ? messageData.from_wa_id 
      : messageData.to_wa_id;
    
    const phoneNumberId = messageData.phone_number_id;
    const chatId = `${customerPhone}_${phoneNumberId}`;
    
    // Check if this is the selected chat (don't increment unread if it is)
    const isSelectedChat = selectedChat && 
      selectedChat.customer_phone === customerPhone &&
      selectedChat.phone_number_id === phoneNumberId;
    
    setChats((prevChats) => {
      const existingIndex = prevChats.findIndex(
        (chat) => 
          chat.customer_phone === customerPhone && 
          chat.phone_number_id === phoneNumberId
      );

      const lastMessageAt = messageData.sent_at || new Date().toISOString();
      
      // Calculate unread count
      let unreadCount = 0;
      if (existingIndex >= 0) {
        unreadCount = prevChats[existingIndex].unread || 0;
        // Only increment if inbound message and not selected chat
        if (messageData.direction === "inbound" && !isSelectedChat) {
          unreadCount += 1;
        }
      } else {
        // New conversation - only count if inbound
        unreadCount = messageData.direction === "inbound" ? 1 : 0;
      }
      
      const formattedChat = {
        id: chatId,
        customer_phone: customerPhone,
        phone_number_id: phoneNumberId,
        name: messageData.customer_name || customerPhone,
        phone: customerPhone,
        lastMessage: formatLastMessageText({
          message_text: messageData.message_text,
          message_type: messageData.message_type,
          media_url: messageData.media_url,
        }),
        timestamp: formatTimestamp(lastMessageAt),
        unread: unreadCount,
        is_window_open: existingIndex >= 0 
          ? prevChats[existingIndex].is_window_open 
          : true,
        last_message: {
          message_text: messageData.message_text,
          message_type: messageData.message_type,
          sent_at: lastMessageAt,
          media_url: messageData.media_url || null,
          mime_type: messageData.mime_type || null,
        },
        last_message_at: lastMessageAt,
      };

      if (existingIndex >= 0) {
        // Update existing conversation - move to top
        const updated = [...prevChats];
        updated.splice(existingIndex, 1);
        return [formattedChat, ...updated];
      } else {
        // Add new conversation at the top
        return [formattedChat, ...prevChats];
      }
    });
  }, [selectedChat]);

  /**
   * Add message to messages state (for currently selected chat)
   * Note: Real-time cache is handled in socket listener
   */
  const addMessage = useCallback((messageData) => {
    const customerPhone = messageData.direction === "inbound" 
      ? messageData.from_wa_id 
      : messageData.to_wa_id;
    
    // Add to current messages state
    setMessages((prev) => {
      // Check if message already exists to prevent duplicates
      const exists = prev.some(msg => 
        msg.message_id === messageData.message_id || 
        (msg.id && msg.id === messageData.id)
      );
      
      if (exists) {
        return prev;
      }

      // Normalize sent_at for consistent sorting
      let normalizedSentAt = messageData.sent_at;
      if (normalizedSentAt) {
        if (normalizedSentAt instanceof Date) {
          normalizedSentAt = normalizedSentAt.toISOString();
        } else if (typeof normalizedSentAt === 'number') {
          normalizedSentAt = new Date(normalizedSentAt * 1000).toISOString();
        } else if (typeof normalizedSentAt === 'string') {
          const parsed = new Date(normalizedSentAt);
          if (!isNaN(parsed.getTime())) {
            normalizedSentAt = parsed.toISOString();
          }
        }
      }
      
      // Add new message and maintain chronological order (oldest first, newest at bottom)
      const newMessage = {
        ...messageData,
        customer_phone: customerPhone,
        sent_at: normalizedSentAt || messageData.sent_at,
      };
      
      // Add message and sort by sent_at ascending (oldest first, newest at bottom)
      const updated = [...prev, newMessage];
      updated.sort((a, b) => {
        const timeA = new Date(a.sent_at || a.timestamp || 0).getTime();
        const timeB = new Date(b.sent_at || b.timestamp || 0).getTime();
        return timeA - timeB; // Ascending: oldest first, newest at bottom
      });
      
      return updated;
    });
  }, []);

  /**
   * Initialize socket and set up event listeners
   */
  useEffect(() => {
    const socket = initSocket();

    // Listen for new messages
    socket.on("new_message", (messageData) => {
      console.log("📨 New message received:", messageData);
      const customerPhone = messageData.direction === "inbound" 
        ? messageData.from_wa_id 
        : messageData.to_wa_id;
      
      // Update conversation list
      updateConversation(messageData);

      // Always store in real-time cache (even if chat not selected)
      // This ensures messages are available when we fetch later
      const phoneNumberId = messageData.phone_number_id;
      const chatKey = `${customerPhone}_${phoneNumberId}`;
      const realtimeMessages = realtimeMessagesRef.current.get(chatKey) || [];
      const messageExists = realtimeMessages.some(msg => msg.message_id === messageData.message_id);
      
      if (!messageExists) {
        // Normalize sent_at before storing
        let normalizedSentAt = messageData.sent_at;
        if (normalizedSentAt) {
          if (normalizedSentAt instanceof Date) {
            normalizedSentAt = normalizedSentAt.toISOString();
          } else if (typeof normalizedSentAt === 'string') {
            const parsed = new Date(normalizedSentAt);
            if (!isNaN(parsed.getTime())) {
              normalizedSentAt = parsed.toISOString();
            }
          }
        }
        
        const messageWithPhone = {
          ...messageData,
          customer_phone: customerPhone,
          sent_at: normalizedSentAt || messageData.sent_at,
        };
        realtimeMessagesRef.current.set(chatKey, [...realtimeMessages, messageWithPhone]);
        console.log(`💾 Stored real-time message for chat: ${chatKey}`, { sent_at: normalizedSentAt });
      }

      // Add message to current messages state if this is the selected chat
      if (selectedChat && 
          customerPhone === selectedChat.customer_phone &&
          phoneNumberId === selectedChat.phone_number_id) {
        addMessage(messageData);
      }
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.off("new_message");
      }
    };
  }, [selectedChat, updateConversation, addMessage]);

  /**
   * Load selected phone ID from localStorage
   */
  useEffect(() => {
    const savedWaba = localStorage.getItem("selectedWabaAccount");
    if (savedWaba) {
      try {
        const parsed = JSON.parse(savedWaba);
        setSelectedPhoneId(parsed.phoneId || null);
      } catch (e) {
        console.error("Error parsing saved WABA:", e);
      }
    }

    // Listen for WABA account changes
    const handleWabaChange = (event) => {
      if (event.detail && event.detail.phoneId) {
        setSelectedPhoneId(event.detail.phoneId);
      }
    };

    window.addEventListener("wabaAccountChanged", handleWabaChange);

    return () => {
      window.removeEventListener("wabaAccountChanged", handleWabaChange);
    };
  }, []);

  /**
   * Fetch conversations when phone ID changes
   */
  useEffect(() => {
    if (selectedPhoneId) {
      fetchConversations();
    }
  }, [selectedPhoneId, fetchConversations]);

  /**
   * Select chat and fetch messages
   */
  const selectChat = useCallback((chat) => {
    setSelectedChat(chat);
    if (chat) {
      fetchMessages(chat.customer_phone, chat.phone_number_id);
      
      // Reset unread count for this chat
      setChats((prevChats) =>
        prevChats.map((c) =>
          c.id === chat.id ? { ...c, unread: 0 } : c
        )
      );
    } else {
      setMessages([]);
    }
  }, [fetchMessages]);
  
  /**
   * Clear real-time messages for a chat (when messages are confirmed in database)
   * This can be called after a successful fetch to clean up old real-time messages
   */
  const clearRealtimeMessages = useCallback((customerPhone, phoneNumberId) => {
    const chatKey = `${customerPhone}_${phoneNumberId}`;
    realtimeMessagesRef.current.delete(chatKey);
  }, []);
  
  /**
   * Update unread count for a chat
   */
  const updateUnreadCount = useCallback((customerPhone, phoneNumberId, count) => {
    setChats((prevChats) =>
      prevChats.map((c) =>
        c.customer_phone === customerPhone && c.phone_number_id === phoneNumberId
          ? { ...c, unread: count }
          : c
      )
    );
  }, []);

  /**
   * Update a message in messages state
   */
  const updateMessage = useCallback((messageId, updates) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.message_id === messageId ? { ...msg, ...updates } : msg
      )
    );
  }, []);

  const value = {
    // State
    chats,
    selectedChat,
    messages,
    loading,
    messagesLoading,
    selectedPhoneId,
    
    // Actions
    fetchConversations,
    fetchMessages,
    selectChat,
    setSelectedPhoneId,
    addMessage,
    updateConversation,
    updateUnreadCount,
    updateMessage,
  };

  return <LiveChatContext.Provider value={value}>{children}</LiveChatContext.Provider>;
};

/**
 * Hook to use LiveChat context
 */
export const useLiveChat = () => {
  const context = useContext(LiveChatContext);
  if (!context) {
    throw new Error("useLiveChat must be used within LiveChatProvider");
  }
  return context;
};

export default LiveChatContext;

